import { formatStreakMultiplier } from "../game/daily-ignition";
import type { GameSnapshot } from "../game/runtime/types";
import { formatCompact } from "../utils/math";
import { formatDuration } from "./format";
import { eventRailStateText } from "./hud-events";

export interface HudRefs {
  readonly level: HTMLElement;
  readonly title: HTMLElement;
  readonly credits: HTMLElement;
  readonly activeCores: HTMLElement;
  readonly dailyState: HTMLElement;
  readonly lanes: HudLaneRef[];
  readonly railStates: Map<string, HTMLElement>;
  readonly railButtons: Map<string, HTMLElement>;
  readonly panelTargets: Map<string, HTMLElement>;
  readonly feedbackTargets: Map<string, HTMLElement>;
}

export interface HudLaneRef {
  readonly root: HTMLElement;
  readonly value: HTMLElement;
}

export function updateHud(refs: HudRefs, snapshot: GameSnapshot, seenFeedbackId: number): number {
  updateTopHud(refs, snapshot);
  updateCollector(refs, snapshot);
  updatePanelTargets(refs, snapshot);
  updateRailButtons(refs, snapshot);
  updateDailyChip(refs, snapshot);
  return applyHudFeedback(refs, snapshot, seenFeedbackId);
}

function updateTopHud(refs: HudRefs, snapshot: GameSnapshot): void {
  setText(refs.level, snapshot.progress.prestigeLevel > 0 ? `P${snapshot.progress.prestigeLevel}` : `LVL ${snapshot.progress.level}`);
  setText(refs.title, snapshot.progress.title);
  setText(refs.credits, formatCompact(snapshot.save.stats.Credits));
  setText(refs.activeCores, `${snapshot.arena.activeProgressCores}/${snapshot.effects.maxCores}`);
  setText(refs.dailyState, dailyStateText(snapshot));
}

function dailyStateText(snapshot: GameSnapshot): string {
  if (snapshot.daily.available) {
    return "AVAILABLE";
  }
  if (snapshot.daily.active) {
    return `${formatStreakMultiplier(snapshot.daily.multiplier)} ${formatDuration(snapshot.daily.remainingSeconds)}`;
  }
  return "";
}

function updateCollector(refs: HudRefs, snapshot: GameSnapshot): void {
  snapshot.arena.lanes.forEach((lane, index) => {
    const ref = refs.lanes[index];
    if (!ref) {
      return;
    }

    const locked = !lane.polarityColor;
    const colorClass = lane.polarityColor ? `of-collector-${lane.polarityColor}` : "of-collector-x1";
    const className = [
      "of-collector-lane",
      locked ? "is-locked" : "",
      snapshot.events.overdrive.active ? "is-overdrive" : "",
      snapshot.events.storm.active ? "is-storm" : "",
      colorClass
    ].filter(Boolean).join(" ");

    setClassName(ref.root, className);
    setText(ref.value, locked ? "LV 3" : lane.label);
    if (lane.lensBonusMultiplier > 1) {
      setDataset(ref.root, "lens", `x${lane.lensBonusMultiplier.toFixed(2)}`);
    } else {
      deleteDataset(ref.root, "lens");
    }
    setAttr(ref.root, "aria-label", collectorAriaLabel(lane.label, lane.polarityColor, lane.lensBonusMultiplier, locked));
  });
}

function collectorAriaLabel(label: string, polarityColor: string | null, lensBonusMultiplier: number, locked: boolean): string {
  if (locked) {
    return "Collector lane multiplier locked, requires level 3";
  }
  if (lensBonusMultiplier > 1) {
    return `${label} ${polarityColor} lens ${lensBonusMultiplier.toFixed(2)}`;
  }
  return label;
}

function updatePanelTargets(refs: HudRefs, snapshot: GameSnapshot): void {
  for (const [id, target] of refs.panelTargets) {
    toggleClass(target, "is-active", snapshot.activePanel === id);
  }
}

function updateRailButtons(refs: HudRefs, snapshot: GameSnapshot): void {
  for (const [id, button] of refs.railButtons) {
    const unlockLevel = Number(button.dataset.unlockLevel ?? "1");
    const locked = snapshot.progress.effectiveLevel < unlockLevel;
    const readyState = eventRailStateText(id, snapshot, button.dataset.readyState ?? "");
    const eventActive = railEventActive(id, snapshot);
    const eventBlocked =
      (id === "overdrive" && snapshot.events.overdrive.blockedByStorm && !snapshot.events.overdrive.active) ||
      (id === "storm" && snapshot.events.storm.blockedByOverdrive && !snapshot.events.storm.active);
    const eventReady = id === "storm" ? snapshot.events.storm.ready : id === "overdrive" ? snapshot.events.overdrive.ready : false;

    toggleClass(button, "is-locked", locked);
    toggleClass(button, "is-active", snapshot.activePanel === id || eventActive);
    toggleClass(button, "is-event-active", eventActive);
    toggleClass(button, "is-event-ready", eventReady);
    toggleClass(button, "is-event-blocked", eventBlocked);
    if (id === "overdrive") {
      setDataset(button, "prestigeOverdrive", (snapshot.progress.prestigeLevel > 0).toString());
    }
    setAttr(button, "aria-disabled", locked || eventBlocked ? "true" : "false");

    const railState = refs.railStates.get(id);
    if (railState) {
      setText(railState, locked ? `REQ LVL ${unlockLevel}` : readyState);
      toggleClass(railState, "is-requirement", locked);
    }

    const actionLabel = button.dataset.actionLabel;
    if (actionLabel) {
      setAttr(button, "aria-label", railActionLabel(actionLabel, readyState, locked, unlockLevel));
    }
  }
}

function railEventActive(id: string, snapshot: GameSnapshot): boolean {
  if (id === "auto") {
    return snapshot.auto.active;
  }
  if (id === "overdrive") {
    return snapshot.events.overdrive.active;
  }
  if (id === "storm") {
    return snapshot.events.storm.active;
  }
  return false;
}

function updateDailyChip(refs: HudRefs, snapshot: GameSnapshot): void {
  const dailyChip = refs.feedbackTargets.get("daily");
  if (!dailyChip) {
    return;
  }

  setHidden(dailyChip, !snapshot.daily.available && !snapshot.daily.active);
  setAttr(dailyChip, "aria-disabled", snapshot.daily.available ? "false" : "true");
  toggleClass(dailyChip, "is-available", snapshot.daily.available);
  toggleClass(dailyChip, "is-active", snapshot.daily.active);
  toggleClass(dailyChip, "is-used", false);
}

function applyHudFeedback(refs: HudRefs, snapshot: GameSnapshot, seenFeedbackId: number): number {
  let highestFeedbackId = seenFeedbackId;
  for (const event of snapshot.feedback) {
    if (event.id <= seenFeedbackId) {
      continue;
    }

    highestFeedbackId = Math.max(highestFeedbackId, event.id);
    if (!event.target) {
      continue;
    }

    const target = refs.feedbackTargets.get(event.target);
    if (!target) {
      continue;
    }

    const className = event.kind === "denied" ? "is-denied" : "is-pulsing";
    target.classList.add(className);
    window.setTimeout(() => target.classList.remove(className), 300);
  }
  return highestFeedbackId;
}

function railActionLabel(actionLabel: string, readyState: string, locked: boolean, unlockLevel: number): string {
  if (locked) {
    return `${actionLabel}, requires level ${unlockLevel}`;
  }

  return readyState ? `${actionLabel}, ${readyState}` : actionLabel;
}

function setText(element: HTMLElement, text: string): void {
  if (element.textContent !== text) {
    element.textContent = text;
  }
}

function setClassName(element: HTMLElement, className: string): void {
  if (element.className !== className) {
    element.className = className;
  }
}

function toggleClass(element: HTMLElement, className: string, enabled: boolean): void {
  if (element.classList.contains(className) !== enabled) {
    element.classList.toggle(className, enabled);
  }
}

function setAttr(element: HTMLElement, name: string, value: string): void {
  if (element.getAttribute(name) !== value) {
    element.setAttribute(name, value);
  }
}

function setDataset(element: HTMLElement, key: string, value: string): void {
  if (element.dataset[key] !== value) {
    element.dataset[key] = value;
  }
}

function deleteDataset(element: HTMLElement, key: string): void {
  if (element.dataset[key] !== undefined) {
    delete element.dataset[key];
  }
}

function setHidden(element: HTMLElement, hidden: boolean): void {
  if (element.hidden !== hidden) {
    element.hidden = hidden;
  }
}
